import React, { FC, Fragment, ReactElement } from 'react';

import { IImage } from '../../types/module';

import { Tag, TagProps } from '../Tag';

import { ButtonV2Props } from '../../../../growth-platform-brand-system-v2/src/components/ButtonV2';
import {
  textBody,
  textUppercaseCaption,
} from '../../../../growth-platform-brand-system-v2/src/styles/typography';
import { classNames } from '../../../../growth-platform-brand-system-v2/src/utils/style';
import {
  CardType,
  applyCapsTextStyle,
  applyCardContentWithoutTagStyle,
  applyCtaButtonStyle,
  applyDisplaySubtitleOnSmallDeviceStyle,
  applyHasSubtitleHoverStyle,
  applyLogoStyle,
  applySubtitleStyle,
  applyTagsStyle,
  applyTitleStyle,
} from './logic';

interface CardContentProps {
  cardType: CardType;
  title: string;
  subtitle?: string;
  hasSubtitleHover?: boolean;
  displaySubtitleOnSmallDevice?: boolean;
  tags?: TagProps[];
  capsText?: string;
  logo?: IImage;
  ctaButton?: ReactElement<ButtonV2Props>;
  isHovered?: boolean;
  TitleTag?: 'h2' | 'h3' | 'div';
  ContentTag?: 'p' | 'div';
}

export const CardContent: FC<CardContentProps> = ({
  cardType,
  title,
  subtitle = '',
  hasSubtitleHover = false,
  displaySubtitleOnSmallDevice = false,
  tags,
  capsText,
  logo,
  ctaButton,
  isHovered = false,
  TitleTag = 'h3',
  ContentTag = 'p',
}) => {
  return (
    <div className="CardContent relative mt-[28px] xl:mt-0">
      {capsText && (
        <div
          className={classNames(
            textUppercaseCaption,
            `text-neutral-101 z-10`,
            applyCapsTextStyle(cardType),
          )}
        >
          {capsText}
        </div>
      )}
      <div
        className={`flex flex-col${applyCardContentWithoutTagStyle(
          !tags,
          cardType,
          !!logo,
        )}`}
      >
        {!!tags?.length && (
          <div
            className={`flex gap-8 flex-wrap overflow-hidden ${applyTagsStyle(
              cardType,
            )}`}
          >
            {tags.map((tag, i) => (
              <Fragment key={tag?.title || `unknow-${i}`}>
                {tag ? <Tag {...tag} /> : null}
              </Fragment>
            ))}
          </div>
        )}
        {logo && (
          <div className={`h-32 ${applyLogoStyle(cardType)}`}>
            <img
              loading="lazy"
              src={logo?.url}
              className="h-32 w-auto"
              alt={logo?.alt || 'logo'}
              width={logo?.width}
              height={logo?.height}
            />
          </div>
        )}
        <TitleTag
          className={`font-medium ${applyTitleStyle(
            cardType,
            hasSubtitleHover,
            isHovered,
          )}`}
        >
          {title}
        </TitleTag>
        {subtitle && (
          <ContentTag
            className={classNames(
              textBody,
              'hidden mt-8 ',
              applySubtitleStyle(cardType),
              displaySubtitleOnSmallDevice &&
                applyDisplaySubtitleOnSmallDeviceStyle(cardType),
              applyHasSubtitleHoverStyle(cardType, hasSubtitleHover),
            )}
          >
            <span
              className={
                hasSubtitleHover
                  ? 'delay-100 opacity-0 md:group-hover:opacity-100 transition-all duration-500 ease-in-out'
                  : ''
              }
            >
              {subtitle}
            </span>
          </ContentTag>
        )}
        {ctaButton && (
          <div className={`flex w-max ${applyCtaButtonStyle(cardType)}`}>
            {ctaButton}
          </div>
        )}
      </div>
    </div>
  );
};
